/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

export const MODAL_CONTROL_PLACEMENT_STORAGE_KEY =
  'qubepro_userSetting_modalControlPlacement';
export const MODAL_CONTROL_PLACEMENT_EVENT =
  'qubepro-modal-control-placement-changed';

export const MODAL_CONTROL_PLACEMENT_OPTIONS = [
  'system',
  'windows',
  'macos',
] as const;

export type ModalControlPlacementPreference =
  (typeof MODAL_CONTROL_PLACEMENT_OPTIONS)[number];
export type ResolvedModalControlPlacement = Exclude<
  ModalControlPlacementPreference,
  'system'
>;

interface PlatformNavigator {
  platform?: string;
  userAgent?: string;
  maxTouchPoints?: number;
  userAgentData?: {
    platform?: string;
  };
}

export function normalizeModalControlPlacement(
  value: unknown,
): ModalControlPlacementPreference {
  const normalized = String(value ?? '').trim().toLowerCase();
  return MODAL_CONTROL_PLACEMENT_OPTIONS.includes(
    normalized as ModalControlPlacementPreference,
  )
    ? (normalized as ModalControlPlacementPreference)
    : 'system';
}

export function detectModalControlPlatform(
  source?: PlatformNavigator,
): ResolvedModalControlPlacement {
  const nav =
    source ??
    (typeof navigator === 'undefined'
      ? undefined
      : (navigator as Navigator & PlatformNavigator));
  if (!nav) return 'windows';

  const userAgent = String(nav.userAgent ?? '');
  const platform = String(nav.userAgentData?.platform ?? nav.platform ?? '');
  const isAppleMobile =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (/^MacIntel$/i.test(platform) && Number(nav.maxTouchPoints ?? 0) > 1);

  return !isAppleMobile && /mac/i.test(`${platform} ${userAgent}`)
    ? 'macos'
    : 'windows';
}

export function resolveModalControlPlacement(
  preference: unknown,
  source?: PlatformNavigator,
): ResolvedModalControlPlacement {
  const normalized = normalizeModalControlPlacement(preference);
  return normalized === 'system'
    ? detectModalControlPlatform(source)
    : normalized;
}

export function readModalControlPlacementPreference():
  ModalControlPlacementPreference {
  if (typeof window === 'undefined') return 'system';
  try {
    return normalizeModalControlPlacement(
      localStorage.getItem(MODAL_CONTROL_PLACEMENT_STORAGE_KEY),
    );
  } catch {
    return 'system';
  }
}

export function applyModalControlPlacementPreference(
  preference: unknown,
): ModalControlPlacementPreference {
  const normalized = normalizeModalControlPlacement(preference);
  if (typeof window === 'undefined') return normalized;

  const resolved = resolveModalControlPlacement(normalized);
  const root = document.documentElement;
  const previousPreference = root.dataset.modalControlPreference;
  const previousResolved = root.dataset.modalControlPlacement;

  root.dataset.modalControlPreference = normalized;
  root.dataset.modalControlPlacement = resolved;
  try {
    localStorage.setItem(MODAL_CONTROL_PLACEMENT_STORAGE_KEY, normalized);
  } catch {
    // Tercih hassas değildir; storage kapalıysa yalnız mevcut oturumda uygulanır.
  }

  if (previousPreference !== normalized || previousResolved !== resolved) {
    window.dispatchEvent(
      new CustomEvent(MODAL_CONTROL_PLACEMENT_EVENT, {
        detail: { preference: normalized, resolved },
      }),
    );
  }

  return normalized;
}
